// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

// api
import { useGetUserByEmailQuery } from '@/api/recalc/endpoints/userApiSlice';

// shared components
import MoonIcon from '@/components/ui/icon/MoonIcon';
import SunIcon from '@/components/ui/icon/SunIcon';
import SunsetIcon from '@/components/ui/icon/SunsetIcon';

function getCurrentHour(timezone: string) {
  try {
    const formattedHour = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      hour12: false,
      timeZone: timezone || undefined,
    }).format(new Date());

    const parsedHour = Number(formattedHour);
    return Number.isNaN(parsedHour) ? new Date().getHours() : parsedHour;
  } catch {
    return new Date().getHours();
  }
}

interface HomeGreetingBadgeProps {
  email: string;
}

export default function HomeGreetingBadge({ email }: HomeGreetingBadgeProps) {
  const theme = useTheme();
  const { t: tToolbar } = useTranslation('tool-bar');
  const { data: userData } = useGetUserByEmailQuery(email, { skip: !email });
  const timezone = userData?.TimeZone || '';

  const currentHour = getCurrentHour(timezone);

  const greetingText =
    currentHour < 12
      ? tToolbar('toolbar.greeting.morning')
      : currentHour < 18
        ? tToolbar('toolbar.greeting.afternoon')
        : tToolbar('toolbar.greeting.evening');

  const GreetingIcon =
    currentHour < 12 ? SunIcon : currentHour < 18 ? SunsetIcon : MoonIcon;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        mb: 3,
        pl: 0.5,
        pr: 2,
        py: 0.5,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: alpha(theme.palette.text.primary, 0.03),
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.5,
          py: 0.5,
          borderRadius: 2,
          fontSize: 14,
          fontWeight: 700,
          lineHeight: 1.5,
          bgcolor: theme.palette.mode === 'dark' ? 'common.white' : 'grey.900',
          color:
            theme.palette.mode === 'dark' ? 'common.black' : 'common.white',
        }}
      >
        <GreetingIcon sx={{ fontSize: 18 }} />
        {greetingText}
      </Box>
      <Typography variant="body1" fontWeight={500} color="text.secondary">
        {email}
      </Typography>
    </Box>
  );
}
